import { Link } from 'react-router-dom';
import './PrivacyPolicyPage.css';

const PrivacyPolicyPage = () => {
  return (
    <div className="privacy-policy-page">
      <div className="privacy-policy-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated"><strong>Last updated:</strong> November 27, 2025</p>

        <section className="privacy-section">
          <h2>Introduction</h2>
          <p>
            Welcome to Storywriter AI. This Privacy Policy explains how we collect, use, disclose,
            and safeguard your information when you use our application. Please read this privacy
            policy carefully. By using Storywriter AI, you agree to the collection and use of
            information in accordance with this policy.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Information We Collect</h2>
          <h3>Google Account Information</h3>
          <p>When you sign in with Google, we collect the following information from your Google account:</p>
          <ul>
            <li><strong>Email address:</strong> Used to identify your account and communicate with you</li>
            <li><strong>Name:</strong> Used to personalize your experience</li>
            <li><strong>Profile picture:</strong> Displayed in the app interface</li>
            <li><strong>Google Account ID (sub):</strong> A unique identifier used to associate your data</li>
          </ul>

          <h3>User-Generated Content</h3>
          <p>We store the content you upload and create within the app, including:</p>
          <ul>
            <li>Text chunks and documents you upload</li>
            <li>Search queries and selected chunks</li>
            <li>Session data and preferences</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>How We Use Your Information</h2>
          <p>We use your information solely to:</p>
          <ul>
            <li>Authenticate your identity and provide secure access to your account</li>
            <li>Store and retrieve your data within the app</li>
            <li>Provide personalized app functionality</li>
            <li>Manage your subscription and storage limits</li>
            <li>Improve our services and user experience</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Google User Data</h2>
          <p>Our use of Google user data is limited to the following:</p>
          <ul>
            <li>We only request the minimum necessary scopes (email, profile) for authentication</li>
            <li>We do not share your Google user data with third parties</li>
            <li>We do not use your Google user data for advertising purposes</li>
            <li>We store your Google user ID to associate your data within our application</li>
          </ul>
          <p>
            Our use of information received from Google APIs adheres to the{' '}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Data Storage and Security</h2>
          <p>
            Your data is stored securely using industry-standard cloud services. We implement
            appropriate technical and organizational measures to protect your personal information
            against unauthorized access, alteration, disclosure, or destruction.
          </p>
          <ul>
            <li>Data is encrypted in transit using HTTPS/TLS</li>
            <li>Data is stored in secure cloud infrastructure (AWS)</li>
            <li>Access to your data is restricted to your authenticated account only</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Data Sharing</h2>
          <p>
            We do not sell, trade, or otherwise transfer your personal information to third parties.
            Your data remains private and is only accessible by you through your authenticated account.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Data Retention</h2>
          <p>
            We retain your personal information for as long as your account is active or as needed
            to provide you services. If you request deletion of your account, we will delete your
            data within 30 days, except where we are required to retain it for legal purposes.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Data Deletion</h2>
          <p>
            You have the right to request deletion of your data at any time. To request deletion of
            your data, please contact us at{' '}
            <a href="mailto:andrewcbuensalida@gmail.com">andrewcbuensalida@gmail.com</a>. We will
            process your request and delete your data within 30 days.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Your Rights</h2>
          <p>You have the following rights regarding your personal data:</p>
          <ul>
            <li><strong>Access:</strong> You can access your data through the app interface</li>
            <li><strong>Correction:</strong> You can update your information through your Google account</li>
            <li><strong>Deletion:</strong> You can request deletion of your data by contacting us</li>
            <li><strong>Portability:</strong> You can export your data using the Export feature in the app</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes
            by posting the new Privacy Policy on this page and updating the "Last updated" date.
            You are advised to review this Privacy Policy periodically for any changes.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our data practices, please
            contact us at:
          </p>
          <p>
            <strong>Email:</strong>{' '}
            <a href="mailto:andrewcbuensalida@gmail.com">andrewcbuensalida@gmail.com</a>
          </p>
        </section>

        <div className="back-link">
          <Link to="/">← Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
